import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import { useUser } from 'hooks/useUser';
import { Link } from 'react-router';
import { useGetTemplatesUsingQuestionQuery } from 'rtk/query/apis/questions';
import { isHubertAdminUser } from 'types/user.types';
import { useQuestionEditorContext } from '../context/create-question.context';

const QuestionInTemplatesList = () => {
  const { t } = useTranslation('Questionsv2');
  const { questionId } = useQuestionEditorContext();
  const user = useUser();
  const { data, isLoading } = useGetTemplatesUsingQuestionQuery({ questionId }, { skip: !questionId });
  if (isLoading)
    return (
      <div className={clsx('flex min-h-[60vh] items-center justify-center px-4')}>
        <div className={clsx('flex items-center gap-3 text-gray-600')}>
          <span className={clsx('h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500')} />
          <span className={clsx('text-sm font-medium')}>{t('questionInTemplates.loading')}</span>
        </div>
      </div>
    );
  const templates = data?.templates ?? [];

  if (!templates.length) {
    return (
      <div className={clsx('flex min-h-[60vh] items-center justify-center px-4')}>
        <div className={clsx('w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm')}>
          <div
            className={clsx(
              'mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200',
            )}
          >
            <span className={clsx('text-xl text-gray-400')}>ⓘ</span>
          </div>
          <p className={clsx('font-semibold text-gray-700')}>{t('questionInTemplates.empty.title')}</p>
          <p className={clsx('mt-1 text-sm text-gray-500')}>{t('questionInTemplates.empty.subtitle')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('col-span-2')}>
      <div className={clsx('mx-auto max-w-7xl')}>
        <div className={clsx('my-4 sm:my-6')}>
          <p className={clsx('text-sm text-gray-500')}>{t('questionInTemplates.description')}</p>
          {/* TODO(i18n): Category 4 — "Only templates that are <strong>currently active or drafts</strong> are listed here." has inline <strong> splitting the sentence */}
          <p className={clsx('text-sm text-gray-500')}>
            Only templates that are <strong>currently active or drafts</strong> are listed here.
          </p>
        </div>
        <div className={clsx('rounded-xl border border-gray-200 bg-white shadow-sm')}>
          <div className={clsx('relative max-h-[60vh] overflow-auto')}>
            <table className={clsx('w-full min-w-5xl table-auto')}>
              <thead>
                <tr className={clsx('')}>
                  <th
                    scope="col"
                    className={clsx(
                      'sticky -top-px z-10 w-12 rounded-tl-xl border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold tracking-wider whitespace-nowrap text-gray-600 uppercase',
                    )}
                  >
                    {t('questionInTemplates.columns.index')}
                  </th>
                  {isHubertAdminUser(user) && (
                    <th
                      scope="col"
                      className={clsx(
                        'sticky -top-px z-10 min-w-48 border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold tracking-wider whitespace-nowrap text-gray-600 uppercase',
                      )}
                    >
                      {t('questionInTemplates.columns.client')}
                    </th>
                  )}
                  <th
                    scope="col"
                    className={clsx(
                      'sticky -top-px z-10 min-w-[20rem] border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold tracking-wider whitespace-nowrap text-gray-600 uppercase',
                    )}
                  >
                    {t('questionInTemplates.columns.template')}
                  </th>
                  {isHubertAdminUser(user) && (
                    <th
                      scope="col"
                      className={clsx(
                        'sticky -top-px z-10 min-w-40 border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold tracking-wider whitespace-nowrap text-gray-600 uppercase',
                      )}
                    >
                      {t('questionInTemplates.columns.processId')}
                    </th>
                  )}
                  <th
                    scope="col"
                    className={clsx(
                      'sticky -top-px z-10 min-w-32 rounded-tr-xl border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold tracking-wider whitespace-nowrap text-gray-600 uppercase',
                    )}
                  >
                    {t('questionInTemplates.columns.status')}
                  </th>
                </tr>
              </thead>
              <tbody className={clsx('divide-y divide-gray-200')}>
                {templates.map((tmpl, index) => (
                  <tr key={`${tmpl._id}-${tmpl.process_id}`} className={clsx('text-sm hover:bg-gray-50')}>
                    <td className={clsx('w-12 px-4 py-3 align-middle whitespace-nowrap')}>{index + 1}</td>
                    {isHubertAdminUser(user) && (
                      <td className={clsx('max-w-88 truncate px-4 py-3 align-middle whitespace-nowrap')}>
                        {tmpl.client?.org_name ?? t('questionInTemplates.noClient')}
                      </td>
                    )}
                    <td className={clsx('max-w-[24rem] px-4 py-3 align-middle whitespace-nowrap')}>
                      <Link
                        to={
                          tmpl.is_draft
                            ? `/templates/${tmpl.template_reference_id}/draft`
                            : `/templates/${tmpl.template_reference_id}`
                        }
                        className={clsx(
                          'block max-w-full truncate text-sm font-medium',
                          'text-blue-600 transition hover:text-blue-700',
                        )}
                        title={tmpl.name}
                      >
                        {tmpl.name}
                      </Link>
                    </td>
                    {isHubertAdminUser(user) && (
                      <td className={clsx('px-4 py-3 align-middle whitespace-nowrap')}>
                        <HubertBadge variant={'info'}>{tmpl.process_id}</HubertBadge>
                      </td>
                    )}
                    <td className={clsx('px-4 py-3 align-middle whitespace-nowrap')}>
                      <HubertBadge variant={tmpl.is_current ? 'success' : tmpl.is_draft ? 'info' : 'destructive'}>
                        {tmpl.is_current
                          ? t('questionInTemplates.status.current')
                          : tmpl.is_draft
                            ? t('questionInTemplates.status.draft')
                            : t('questionInTemplates.status.other')}
                      </HubertBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionInTemplatesList;
